import React, { useState } from 'react';
import { Star, X, ThumbsUp, MessageSquare } from 'lucide-react';

interface RatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderNumber: string;
  onSubmitRating: (rating: number, comment: string) => void;
}

const RatingModal: React.FC<RatingModalProps> = ({ 
  isOpen, 
  onClose, 
  orderNumber, 
  onSubmitRating 
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) return;

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    onSubmitRating(rating, comment);
    setIsSubmitting(false);
    onClose();
    
    // Reset form
    setRating(0);
    setComment('');
  };

  const getRatingText = (stars: number) => {
    switch (stars) {
      case 5: return 'Excellent ! 🎉';
      case 4: return 'Très bien ! 👍';
      case 3: return 'Bien 👌';
      case 2: return 'Peut mieux faire 😐';
      case 1: return 'Décevant 😞';
      default: return 'Cliquez sur les étoiles';
    }
  };

  const getRatingColor = (stars: number) => {
    if (stars >= 4) return 'text-green-600';
    if (stars === 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-black">Évaluez votre expérience</h2>
            <p className="text-sm text-gray-600">Commande #{orderNumber}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Thank you message */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ThumbsUp className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-2">
              Merci pour votre commande !
            </h3>
            <p className="text-gray-600 text-sm">
              Votre avis nous aide à améliorer notre service
            </p>
          </div>

          {/* Star Rating */}
          <div className="text-center mb-6">
            <p className="text-sm font-medium text-gray-700 mb-4">
              Comment évaluez-vous votre expérience ?
            </p>
            
            <div className="flex justify-center space-x-2 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="p-1 transition-all duration-200 hover:scale-110"
                >
                  <Star 
                    className={`w-10 h-10 transition-colors duration-200 ${
                      star <= (hoveredRating || rating) 
                        ? 'text-orange-400 fill-orange-400' 
                        : 'text-gray-300 hover:text-orange-300'
                    }`} 
                  />
                </button>
              ))}
            </div>
            
            <p className={`text-sm font-medium transition-colors duration-200 ${
              rating > 0 ? getRatingColor(rating) : 'text-gray-400'
            }`}>
              {getRatingText(hoveredRating || rating)}
            </p>
          </div>

          {/* Comment Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MessageSquare className="w-4 h-4 inline mr-1" />
              Commentaire (optionnel)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Partagez votre expérience avec nous..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 resize-none"
              rows={4}
              maxLength={500}
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-gray-500">
                Votre avis nous aide à nous améliorer
              </p>
              <p className="text-xs text-gray-400">
                {comment.length}/500
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={rating === 0 || isSubmitting}
            className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 shadow-sm hover:shadow-md ${
              rating === 0 || isSubmitting
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-orange-500 text-white hover:bg-orange-600'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Envoi en cours...</span>
              </div>
            ) : (
              'Envoyer l\'évaluation'
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-3">
            Votre évaluation sera visible publiquement
          </p>
        </form>
      </div>
    </div>
  );
};

export default RatingModal;